/**
 * Real shadow-preview route.
 *
 * Phase 6 scaffold. The server-side route does not exist yet; this
 * file is wired so tests can stub responses today and the symbol swap
 * in `client.ts` is already in place.
 *
 *   POST /gi/{root_id}/preview
 *     Body: { inputs_hex: Record<string, string> }
 *     → PreviewResult
 *
 * A "preview" is a shadow execution: the engine runs the GI with the
 * caller's inputs but does NOT append the run to the lineage log. The
 * response always carries the shadow outputs; when a recent real run
 * exists for the same input set, the engine additionally returns the
 * real outputs so the UI can render a diff view.
 */

import type { EntityId } from '../types';
import { apiFetch } from '../http';

export interface PreviewOutputs {
  /** Hex-encoded outputs keyed by port name. */
  outputs_hex: Record<string, string>;
  /** Fuel units consumed during the shadow run. */
  fuel_consumed: number;
  /** Wall-clock duration of the shadow run, in nanoseconds. */
  duration_ns: number;
}

export interface PreviewResult {
  /** The shadow execution always runs — this is always populated. */
  shadow: PreviewOutputs;
  /**
   * The most recent real (lineage-committed) execution against the
   * same inputs, when one is available within the server's cache
   * window. Absent when no matching real run is cached — the UI
   * renders a "no real run to diff against" state.
   */
  real?: PreviewOutputs;
}

export async function previewGi(
  root_id: EntityId,
  inputs_hex: Record<string, string>,
): Promise<PreviewResult> {
  return await apiFetch<PreviewResult>(
    `/gi/${encodeURIComponent(root_id)}/preview`,
    { method: 'POST', body: { inputs_hex } },
  );
}

/** Compare shadow vs real outputs. Returns the set of port names that
 *  differ, plus a convenience `equal` flag. When the preview result
 *  has no real run, every port reads as "shadow only" and `equal` is
 *  false. */
export function diffPreview(result: PreviewResult): {
  equal: boolean;
  differing_ports: string[];
  shadow_only: boolean;
} {
  if (!result.real) {
    return {
      equal: false,
      differing_ports: Object.keys(result.shadow.outputs_hex),
      shadow_only: true,
    };
  }
  const differing: string[] = [];
  const keys = new Set([
    ...Object.keys(result.shadow.outputs_hex),
    ...Object.keys(result.real.outputs_hex),
  ]);
  for (const k of keys) {
    if (result.shadow.outputs_hex[k] !== result.real.outputs_hex[k]) {
      differing.push(k);
    }
  }
  return {
    equal: differing.length === 0,
    differing_ports: differing,
    shadow_only: false,
  };
}
